import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { moneyTight as fmtAmt, fmtNumber } from '../../utils/region';

export type CloseCheck = { ok: boolean; gated: boolean; archiveDone: boolean; calendarDone: boolean; missingDocs: string[]; checkUnavailable: boolean; owed: boolean; balanceDue: number };

// ── One prompt per job (Calem, report dd0ed51a) ─────────────────────────────────────────────────
// Two things mount this now: the job panel, for the job on screen, and the app-wide watcher
// (components/CloseJobWatcher), for a job that became closable while the operator was elsewhere. Open
// the very job the watcher is asking about and both would be alive — two identical dialogs stacked.
// So every visible prompt records its job here under its own tag, and the watcher stands down for a
// job another prompt is already asking about: the one the operator is looking at wins.
export const PANEL_TAG = 'panel';
const showing = new Map<string, Set<string>>();
const listeners = new Set<() => void>();
export const closePromptHeldByOther = (jobId: string, tag: string): boolean =>
  Array.from(showing.get(jobId) || []).some(x => x !== tag);
/** Tells the watcher the moment a prompt appears or goes, so it never waits a poll to stand down. */
export const onClosePromptChange = (fn: () => void): (() => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
const hold = (jobId: string, tag: string) => {
  const s = showing.get(jobId) || new Set<string>();
  s.add(tag); showing.set(jobId, s);
  listeners.forEach(f => f());
};
const release = (jobId: string, tag: string) => {
  const s = showing.get(jobId);
  if (!s) return;
  s.delete(tag);
  if (!s.size) showing.delete(jobId);
  listeners.forEach(f => f());
};

/** A save that closed a job whose Re-entry Stamp job follows it (report 819e9a8d) names the stamp jobs their
 *  own close check kept open (reentryNotClosed). The job panel's status picker and Save say it too. */
export const reentryNotClosedNote = (t: any, nos: any[]): string =>
  t('jb_reentry_not_closed', { jobs: nos.map(n => `#${fmtNumber(n)}`).join(', ') });

// "Ready to close this job?" prompt (Calem, bug 3abdfed9 follow-up): once a job is 4. Complete AND
// its client status-update email has actually been sent, offer to move it straight to 6. Closed —
// reusing the exact gate the manual Close button enforces (routes/jobs.ts closeGate, via the new
// read-only /close-check) so the prompt shows what's still missing up front, same wording the
// operator would otherwise only see after clicking Close and being refused.
// Dismissing is session-only (no persisted "seen" flag, by design) — it asks again next time this
// job is opened, same as the operator not having closed it yet.
// `given` / `identity` / `zIndex` are the app-wide watcher's (report dd0ed51a): its poll already
// carries the gate's answer, and because the operator is NOT looking at the job it has to say which
// job this is about. Omitted — the job panel's call — nothing changes: it fetches its own check and
// shows no identity line, because the job is right there on screen.
export default function CloseNowPrompt({ jobId, processLevel, completeEmailSentAt, onClosed, onDismissed, check: given, identity, tag = PANEL_TAG, zIndex = 1200 }: {
  jobId: string; processLevel: string; completeEmailSentAt: number; onClosed: () => void;
  onDismissed?: () => void; check?: CloseCheck; identity?: string; tag?: string; zIndex?: number;
}) {
  const { t } = useTranslation();
  const due = processLevel === '4. Complete' && completeEmailSentAt > 0;
  const [dismissed, setDismissed] = useState(false);
  const [fetched, setFetched] = useState<CloseCheck | null>(null);
  const [closing, setClosing] = useState(false);
  const [err, setErr] = useState('');
  const check = given || fetched;

  useEffect(() => { setDismissed(false); setFetched(null); setErr(''); }, [jobId]);
  useEffect(() => {
    if (!due || dismissed || given) return;
    let alive = true;
    api.jobCloseCheck(jobId).then((r: any) => { if (alive) setFetched(r); }).catch(() => { /* stay silent — the manual Close button still works */ });
    return () => { alive = false; };
  }, [jobId, due, dismissed, given]);

  // Registered while on screen, dropped the moment it goes (see the registry above).
  const shown = due && !dismissed && !!check;
  useEffect(() => {
    if (!shown) return;
    hold(jobId, tag);
    return () => release(jobId, tag);
  }, [shown, jobId, tag]);

  if (!check || !shown) return null;
  // Only a gated job has a closing checklist; an ungated one can now fail the check on money alone.
  const checklistOpen = check.gated && (!check.archiveDone || !check.calendarDone || check.missingDocs.length > 0);
  // "Not now" / "Got it" / clicking away. The watcher is told, so it can move on to the next job it
  // was holding instead of sitting silently on the one that was just waved off.
  const dismiss = () => { setDismissed(true); onDismissed?.(); };

  const doClose = async () => {
    if (closing) return;
    setClosing(true); setErr('');
    try {
      const r: any = await api.updateJob(jobId, { processLevel: '6. Closed' });
      if (r?.reentryNotClosed?.length) window.alert(reentryNotClosedNote(t, r.reentryNotClosed));
      setDismissed(true); onClosed();
    }
    catch (e: any) { setErr(e?.message || 'Failed'); }
    finally { setClosing(false); }
  };

  const btn: React.CSSProperties = { padding: '7px 16px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
  const ghost: React.CSSProperties = { padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex }}
      onMouseDown={e => { if (e.target === e.currentTarget && !closing) dismiss(); }}>
      <div style={{ width: 440, maxWidth: '92vw', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <i className="ti ti-circle-check" style={{ fontSize: 18, color: check.ok ? '#15803D' : '#B45309' }} aria-hidden />
          <div style={{ fontSize: 15, fontWeight: 800 }}>{t('jb_close_prompt_title')}</div>
        </div>
        {/* Which job — only the watcher sends this: the operator was somewhere else when it popped. */}
        {identity && <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, lineHeight: 1.5, wordBreak: 'break-word' }}>{t('jb_close_prompt_job', { job: identity })}</div>}
        {check.ok ? (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{t('jb_close_prompt_ready')}</div>
        ) : (
          <>
            {/* Money still owed blocks Closed on every job (Calem, report 6585fc5a) — no Close button. */}
            {check.owed && <div style={{ fontSize: 13, fontWeight: 700, color: '#DC2626', lineHeight: 1.5, marginBottom: check.checkUnavailable || checklistOpen ? 8 : 0 }}>{t('jb_close_owed', { amt: fmtAmt(check.balanceDue) })}</div>}
            {check.checkUnavailable ? (
              <div style={{ fontSize: 13, color: '#92400E', lineHeight: 1.5 }}>{t('jb_close_prompt_unavailable')}</div>
            ) : checklistOpen && (
              <>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{t('jb_close_prompt_missing_intro')}</div>
                <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 12.5, color: '#92400E' }}>
                  {!check.archiveDone && <li>{t('jb_close_prompt_missing_files')}</li>}
                  {!check.calendarDone && <li>{t('jb_close_prompt_missing_calendar')}</li>}
                  {check.missingDocs.length > 0 && <li>{t('jb_close_prompt_missing_docs_prefix')}{check.missingDocs.join('; ')}</li>}
                </ul>
              </>
            )}
          </>
        )}
        {err && <div style={{ fontSize: 12, color: '#E24B4A', marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button onClick={() => !closing && dismiss()} style={ghost}>{check.ok ? t('jb_close_prompt_not_now') : t('jb_close_prompt_got_it')}</button>
          {check.ok && <button onClick={doClose} disabled={closing} style={{ ...btn, opacity: closing ? 0.6 : 1 }}>{closing ? '…' : t('jb_close_prompt_close_btn')}</button>}
        </div>
      </div>
    </div>
  );
}
